import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ControlsContainerProps } from './ControlsContainer.types';
import './ControlsContainer.styles.css';

export const ControlsContainer: React.FC<ControlsContainerProps> = ({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  sortOrder,
  onSortOrderChange,
  sortOptions,
  activeFilter,
  onFilterChange,
  filterOptions,
  showFilters,
  resultsCount,
  searchPlaceholder = 'Rechercher...',
  className = '',
  children,
  afterFilters,
  filtersDisabled = false
}) => {
  return (
    <div className={`controls-container ${className}`}>
      <div className="controls-container__row">
        {/* Search Bar */}
        <div className="controls-container__search">
          <input
            type="text"
            className="controls-container__search-input"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <svg
            className="controls-container__search-icon"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM15 15l4 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {searchQuery && (
            <button
              type="button"
              className="controls-container__clear-btn"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
            >
              <XMarkIcon className="controls-container__clear-icon" />
            </button>
          )}
        </div>

        {/* Sort Dropdown with Order Toggle */}
        <div className="controls-container__sort">
          <select
            className="controls-container__select"
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {/* Sort order toggle button */}
          {onSortOrderChange && (
            <button
              className="controls-container__sort-order"
              onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={sortOrder === 'asc' ? 'Tri croissant' : 'Tri décroissant'}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                {sortOrder === 'asc' ? (
                  // Ascending arrow
                  <>
                    <path d="M12 20V4"/>
                    <path d="M6 10l6-6 6 6"/>
                  </>
                ) : (
                  // Descending arrow
                  <>
                    <path d="M12 4v16"/>
                    <path d="M6 14l6 6 6-6"/>
                  </>
                )}
              </svg>
            </button>
          )}
        </div>

        {/* Results Count */}
        <div className="controls-container__count">
          <span className="controls-container__count-number">{resultsCount}</span>
          <span className="controls-container__count-label">résultats</span>
        </div>
      </div>

      {/* Filter Tabs */}
      {showFilters && (
        <div className={`controls-container__filters ${filtersDisabled ? 'controls-container__filters--disabled' : ''}`}>
          {filterOptions.map(filter => (
            <button
              key={filter.key}
              className={`controls-container__filter-tab ${activeFilter === filter.key ? 'active' : ''}`}
              onClick={() => !filtersDisabled && onFilterChange(filter.key)}
              disabled={filtersDisabled}
              title={filtersDisabled ? 'Filtres désactivés pendant la recherche' : undefined}
            >
              {filter.label}
              {filter.notificationCount !== undefined && filter.notificationCount > 0 && (
                <span className="controls-container__filter-badge">
                  {filter.notificationCount}
                </span>
              )}
            </button>
          ))}

          {/* Additional content after filter tabs */}
          {afterFilters}
        </div>
      )}

      {/* Expanded content sections */}
      {children}
    </div>
  );
};